var _react_refresh_temp_1;
_react_refresh_temp_1 = $RefreshSig$();
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { FsEntryType } from '@lucidlink/sdk';
import { compareBrowserEntriesByTypeThenName } from '@lucidlink/uicore';
import { useFsEntryGetAllContents, useFsEntryGetKey, useFsEntryGetPath, } from '#query-hooks';
import { FILESPACE_ROOT_ID, ROOT_DIR_PATH } from '../../constants';
import { findOpenedDirPath, getLastPathSegment, remapFsEntryToBrowserEntry, } from '../helpers';
import { useWebFsNavigationErrors } from '../useWebFsNavigationErrors';
import { seedOpenedDirCache } from './helpers';
import { useWebFsCrumbs } from './useWebFsCrumbs';
export const useWebFsNavigation = ({ browserInstance, currentDirId, filespaceId, rootName, setCurrentDirId, workspaceId, }) => {
    _react_refresh_temp_1();
    const queryClient = useQueryClient();
    const { query: { data: filespaceRootKey, error: errorFilespaceRootKey, isFetching: isFetchingFilespaceRootKey, isPending: isPendingFilespaceRootKey, refetch: refetchFilespaceRootKey, }, } = useFsEntryGetKey({
        filespaceId,
        fsEntryPath: ROOT_DIR_PATH,
        workspaceId,
    });
    const currentDirKey = currentDirId
        ? {
            id: currentDirId,
            type: FsEntryType.DIR,
        }
        : filespaceRootKey;
    const { query: { data: currentDirPath, error: errorCurrentDirPath, isFetching: isFetchingCurrentDirPath, isPending: isPendingCurrentDirPath, refetch: refetchCurrentDirPath, }, } = useFsEntryGetPath({
        filespaceId,
        key: currentDirKey,
        workspaceId,
    });
    const { query: { data: currentDirContents, error: errorCurrentDirContents, isFetching: isFetchingCurrentDirContents, isPending: isPendingCurrentDirContents, refetch: refetchCurrentDirContents, }, } = useFsEntryGetAllContents({
        browserInstance,
        filespaceId,
        key: currentDirKey,
        workspaceId,
    });
    const { crumbPrefixes, crumbs, isCrumbsError, isCrumbsPending } = useWebFsCrumbs({
        currentDirPath,
        filespaceId,
        filespaceRootKey: filespaceRootKey ?? undefined,
        rootName,
        workspaceId,
    });
    const navigationQueries = useMemo(() => [
        {
            error: errorFilespaceRootKey,
            isFetching: isFetchingFilespaceRootKey,
            refetch: refetchFilespaceRootKey,
        },
        {
            error: errorCurrentDirPath,
            isFetching: isFetchingCurrentDirPath,
            refetch: refetchCurrentDirPath,
        },
        {
            error: errorCurrentDirContents,
            isFetching: isFetchingCurrentDirContents,
            refetch: refetchCurrentDirContents,
        },
    ], [
        errorCurrentDirContents,
        errorCurrentDirPath,
        errorFilespaceRootKey,
        isFetchingCurrentDirContents,
        isFetchingCurrentDirPath,
        isFetchingFilespaceRootKey,
        refetchCurrentDirContents,
        refetchCurrentDirPath,
        refetchFilespaceRootKey,
    ]);
    const { errors, isRetrying, retry } = useWebFsNavigationErrors(navigationQueries);
    const remappedDirContents = useMemo(() => currentDirContents
        ?.map(remapFsEntryToBrowserEntry)
        .sort(compareBrowserEntriesByTypeThenName), [currentDirContents]);
    const changeCurrentDir = useCallback((dirId) => {
        // The root has a real id, but it is stored as FILESPACE_ROOT_ID so the root
        // is addressed by a single URL rather than both "" and its own id
        if (dirId === filespaceRootKey?.id) {
            setCurrentDirId(FILESPACE_ROOT_ID);
            return;
        }
        // The clicked entry already tells us the target path, so seed the
        // path and key caches to keep the breadcrumbs from flashing
        const openedDirPath = findOpenedDirPath({
            crumbPrefixes,
            crumbs,
            currentDirContents,
            currentDirPath,
            dirId,
        });
        if (openedDirPath !== undefined) {
            seedOpenedDirCache({
                dirId,
                dirPath: openedDirPath,
                filespaceId,
                queryClient,
            });
        }
        setCurrentDirId(dirId);
    }, [
        crumbPrefixes,
        crumbs,
        currentDirContents,
        currentDirPath,
        filespaceId,
        filespaceRootKey?.id,
        queryClient,
        setCurrentDirId,
    ]);
    const currentDirName = currentDirPath === ROOT_DIR_PATH
        ? rootName
        : getLastPathSegment(currentDirPath || '');
    if (errors.length > 0) {
        return {
            changeCurrentDir,
            crumbs: undefined,
            currentDirContents: undefined,
            currentDirPath: undefined,
            errors,
            filespaceRootKey: undefined,
            isCrumbsError,
            isCrumbsPending,
            isError: true,
            isPending: false,
            isRetrying,
            retry,
            currentDirName,
        };
    }
    if (isPendingFilespaceRootKey ||
        isPendingCurrentDirPath ||
        isPendingCurrentDirContents ||
        !filespaceRootKey ||
        currentDirPath === undefined ||
        remappedDirContents === undefined ||
        crumbs === undefined) {
        return {
            changeCurrentDir,
            crumbs,
            currentDirContents: undefined,
            currentDirPath,
            errors,
            filespaceRootKey: undefined,
            isCrumbsError,
            isCrumbsPending,
            isError: false,
            isPending: true,
            isRetrying,
            retry,
            currentDirName,
        };
    }
    return {
        changeCurrentDir,
        crumbs,
        currentDirContents: remappedDirContents,
        currentDirPath,
        errors,
        filespaceRootKey,
        isCrumbsError,
        isCrumbsPending,
        isError: false,
        isPending: false,
        isRetrying,
        retry,
        currentDirName,
    };
};
_react_refresh_temp_1(useWebFsNavigation, "nsHTYGxc7FPCpNoTgA9saKiMttY=", false, () => [useQueryClient, useFsEntryGetKey, useFsEntryGetPath, useFsEntryGetAllContents, useWebFsCrumbs, useWebFsNavigationErrors]);
